import api from '@/services/api';
import { isUuid } from '../utils/isUuid';
import type { AgreementSummary } from '../types/fundAccountPostings.types';
import { serializeBracketedArrayParams } from './bracketedArrayParams';
import { readBoundedNullable } from './periodPayloadChecks';
import { payloadReaders } from './statementPayloadReaders';

export const AGREEMENT_SUMMARIES_MAX_IDS = 50;

const VRM_MAX = 20;
const NAME_MAX = 255;

const { malformed, readRecord, readText, readRealDate, readArray } = payloadReaders('agreement summaries payload');

const mapSummary = (value: unknown): AgreementSummary => {
    const raw = readRecord(value, 'row');
    return {
        agreementId: readText(raw.id, 'id'),
        agreementRef: readText(raw.agreement_ref, 'agreement_ref'),
        vrm: readBoundedNullable(raw.vrm, 'vrm', VRM_MAX),
        customerSurname: readBoundedNullable(raw.customer_surname, 'customer_surname', NAME_MAX),
        soldBy: readBoundedNullable(raw.sold_by, 'sold_by', NAME_MAX),
        soldAt: readRealDate(raw.sold_at, 'sold_at'),
    };
};

const mapSummaries = (body: unknown): AgreementSummary[] => {
    const envelope = readRecord(body, 'response');
    if (envelope.success !== true) throw malformed('success is not true');
    return readArray(envelope.data, 'data').map(mapSummary);
};

export const fetchAgreementSummariesByIds = async (ids: string[]): Promise<AgreementSummary[]> => {
    const unique = Array.from(new Set(ids.filter((id) => isUuid(id)))).sort();
    if (unique.length === 0) return [];
    if (unique.length > AGREEMENT_SUMMARIES_MAX_IDS) {
        throw new Error(`Too many agreement ids: ${unique.length} exceeds ${AGREEMENT_SUMMARIES_MAX_IDS}`);
    }

    const response = await api.get('/agreements/by-ids', {
        params: { ids: unique },
        paramsSerializer: { serialize: serializeBracketedArrayParams },
        suppressGlobalForbiddenToast: true,
    });
    return mapSummaries(response.data);
};
